<script setup lang="ts">
import DeleteButton from "@/components/DeleteButton.vue";
import HandleQueryStatus from "@/components/HandleQueryStatus.vue";
import {
  useDeleteConversation,
  useGetConversations,
} from "@/conversations/conversationsCrud";
import { formatDatetime } from "@/utils/toFormattedDatetime";
import { Heart, NavArrowRight, Trash, User, UserCircle } from "@iconoir/vue";
import { ref } from "vue";
import { RouterLink } from "vue-router";
import type { House } from "./types";

const { house } = defineProps<{
  house: House;
}>();

const { conversations, getConversationsStatus } = useGetConversations(house.id);
const {
  deleteConversation,
  resetDeleteConversation,
  deleteConversationStatus,
} = useDeleteConversation(house.id);

const expandedIds = ref(new Set<string>());

function toggleExpanded(id: string) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
}
</script>
<template>
  <HandleQueryStatus :query-status="getConversationsStatus">
    <div class="container container-lg">
      <table v-if="conversations && conversations.length > 0">
        <thead>
          <tr>
            <th>Erstellt am</th>
            <th><User /> Verknüpfter Kontakt</th>
            <th><UserCircle /> angelegt von</th>
            <th><Heart /> Haustüraktion</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="conversation in conversations"
            :key="conversation.id"
          >
            <tr>
              <td>
                <button
                  class="toggleConversation"
                  :aria-expanded="expandedIds.has(conversation.id)"
                  :aria-controls="`conversation-content-${conversation.id}`"
                  @click="toggleExpanded(conversation.id)"
                >
                  <NavArrowRight
                    :class="{ rotated: expandedIds.has(conversation.id) }"
                  />
                  {{ formatDatetime(conversation.createdAt) }}
                </button>
              </td>
              <td>
                <RouterLink
                  v-if="conversation.contactId"
                  :to="{
                    name: 'editContact',
                    params: { id: conversation.contactId },
                  }"
                  >{{ conversation.contactName }}</RouterLink
                >
                <template v-else>–</template>
              </td>
              <td>{{ conversation.createdByUserName ?? "n/a" }}</td>
              <td>
                <RouterLink
                  :to="{
                    name: 'editCanvass',
                    params: { id: conversation.canvassId },
                  }"
                  >{{ conversation.canvassName }}</RouterLink
                >
              </td>
              <td class="actions">
                <DeleteButton
                  class="button button--tertiary"
                  :deleting-status="deleteConversationStatus"
                  @initiated="resetDeleteConversation()"
                  @confirmed="deleteConversation(conversation.id)"
                >
                  <Trash /><span class="visually-hidden">Löschen</span>
                  <template #confirmation>
                    Möchtest du dieses Gespräch wirklich löschen?
                  </template>
                </DeleteButton>
              </td>
            </tr>
            <tr
              v-if="expandedIds.has(conversation.id)"
              :id="`conversation-content-${conversation.id}`"
              class="conversationContentRow"
            >
              <td colspan="5">
                <p class="conversationContentLabel">Notiz</p>
                <div class="card conversationContent">
                  {{ conversation.content }}
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <p v-else class="box">
        Für dieses Haus wurden noch keine Gespräche hinterlegt.
      </p>
    </div>
  </HandleQueryStatus>
</template>

<style scoped>
.toggleConversation {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  color: inherit;
  font: inherit;

  &:hover {
    background: none;
    text-decoration: underline;
  }

  svg {
    transition: rotate 0.15s ease;

    &.rotated {
      rotate: 90deg;
    }
  }
}

.conversationContentRow {
  background-color: transparent;

  td {
    color: var(--text-color-muted);
    font-size: inherit;
    font-weight: inherit;
  }
}

.conversationContentLabel {
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-label);
  color: var(--text-color-muted);
  margin-block-end: var(--spacing-sm);
}

.conversationContent {
  white-space: pre-wrap;
}
</style>
